import React, { useEffect, useState } from "react";
import { Card, Col, Row, Statistic, Typography, message } from "antd";
import { useTranslation } from "react-i18next";
import Amount from "../shared/Amount";
import { formatCents, formatDate, formatPeriod } from "../shared/money";
import PageToolbar from "../../../../design-system/PageToolbar";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import { errorText } from "../api";
import * as api from "./api";
import { DownloadButton, useLabels, useRange } from "./receivablesUi";

const money = [
  { key: "count", align: "end" },
  { key: "net", align: "end", cents: true },
  { key: "tax", align: "end", cents: true },
  { key: "gross", align: "end", cents: true }
];

/** RP-3: Rechnungskorrekturen by reason, customer and month. */
const CorrectionsTab = () => {
  const { t } = useTranslation("accounting");
  const labels = useLabels();
  const { params, picker } = useRange();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getCorrections(params)
      .then(res => setData(res.data))
      .catch(error => message.error(errorText(error)))
      .finally(() => setLoading(false));
  }, [params]);

  const titles = {
    count: t("Number of documents"),
    net: t("Net"),
    tax: t("VAT"),
    gross: t("Gross")
  };
  const figures = money.map(m => ({
    title: titles[m.key],
    dataIndex: m.key,
    align: m.align,
    render: v => (m.cents ? <Amount cents={v} /> : v)
  }));

  return (
    <div>
      <PageToolbar
        filters={picker}
        actions={
          <DownloadButton
            label={t("Download CSV")}
            promise={() => api.downloadCorrections(params)}
            filename={`Rechnungskorrekturen_${params.from}_${params.to}.csv`}
          />
        }
      />
      <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Invoice corrections")} value={data?.totals?.count || 0} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic
              title={t("Revenue given back (net)")}
              value={formatCents(data?.totals?.net || 0)}
            />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("VAT corrected")} value={formatCents(data?.totals?.tax || 0)} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card size="small">
            <Statistic title={t("Cancelled corrections")} value={data?.cancelledCount || 0} />
          </Card>
        </Col>
      </Row>
      <Typography.Title level={5}>{t("By reason")}</Typography.Title>
      <ResponsiveTable
        mobileLayout="table"
        size="small"
        loading={loading}
        pagination={false}
        rowKey="reason"
        dataSource={data?.byReason || []}
        columns={[
          { title: t("Reason"), dataIndex: "reason", render: v => labels.reason[v] || v },
          ...figures
        ]}
      />
      <Typography.Title level={5} style={{ marginTop: 16 }}>
        {t("By customer")}
      </Typography.Title>
      <ResponsiveTable
        mobileLayout="table"
        size="small"
        loading={loading}
        pagination={false}
        rowKey={r => String(r.customer)}
        dataSource={data?.byCustomer || []}
        columns={[
          { title: t("Customer number"), dataIndex: "customerNumber", width: 110 },
          { title: t("Customer"), dataIndex: "name" },
          ...figures
        ]}
      />
      <Typography.Title level={5} style={{ marginTop: 16 }}>
        {t("By month")}
      </Typography.Title>
      <ResponsiveTable
        mobileLayout="table"
        size="small"
        loading={loading}
        pagination={false}
        rowKey="month"
        dataSource={data?.byMonth || []}
        columns={[{ title: t("Month"), dataIndex: "month", render: formatPeriod }, ...figures]}
      />
      <Typography.Title level={5} style={{ marginTop: 16 }}>
        {t("Documents")}
      </Typography.Title>
      <ResponsiveTable
        mobileLayout="table"
        className="acct-grid"
        size="small"
        loading={loading}
        pagination={{ pageSize: 50, hideOnSinglePage: true }}
        rowKey="id"
        dataSource={data?.documents || []}
        columns={[
          { title: t("Number"), dataIndex: "number" },
          { title: t("Date"), dataIndex: "date", render: formatDate },
          { title: t("Customer"), dataIndex: "customer" },
          {
            title: t("Reason"),
            dataIndex: "reason",
            render: (v, r) => (
              <span className="acct-wrap">
                {labels.reason[v] || v}
                {r.reasonText ? (
                  <Typography.Text type="secondary">{` · ${r.reasonText}`}</Typography.Text>
                ) : null}
              </span>
            )
          },
          { title: t("Refers to"), dataIndex: "original" },
          {
            title: t("Gross"),
            dataIndex: "gross",
            align: "end",
            render: (v, r) =>
              r.cancelled ? (
                <Typography.Text delete>
                  <Amount cents={v} />
                </Typography.Text>
              ) : (
                <Amount cents={v} />
              )
          }
        ]}
      />
    </div>
  );
};

export default CorrectionsTab;
